import React from 'react'
import { graphql, useFragment } from 'react-relay'
import { Trans } from '@lingui/react/macro'

import { YStack, XStack, isWeb, useThemeName } from 'tamagui'
import {
  GuildPrimaryPhoto,
  UserPrimaryPhoto,
  Icon,
  Paragraph,
  Link,
  usePalette,
} from '@newComponents'

import { EventScreenOwner_event$key } from './__generated__/EventScreenOwner_event.graphql'

export const EventScreenOwner = ({
  event,
}: {
  event: EventScreenOwner_event$key
}) => {
  const data = useFragment(
    graphql`
      fragment EventScreenOwner_event on Event {
        owner {
          __typename

          ... on Guild {
            name
            slugId

            primaryPhoto {
              rowId
            }

            ...GuildPrimaryPhotoNew_guild
          }

          ... on User {
            firstName
            lastName

            slugId

            ...UserPrimaryPhotoNew_user
          }
        }

        confirmedHosts {
          edges {
            node {
              id
              host {
                __typename

                ... on Guild {
                  name
                  slugId

                  primaryPhoto {
                    rowId
                  }

                  ...GuildPrimaryPhotoNew_guild
                }

                ... on User {
                  firstName
                  lastName

                  slugId

                  ...UserPrimaryPhotoNew_user
                }
              }
            }
          }
        }
      }
    `,
    event
  )

  const { palette } = usePalette()
  const themeName = useThemeName()
  const isDark = themeName.startsWith('dark')
  const useGlass = !!palette && isWeb

  if (!data.owner) {
    return null
  }

  // Build host list: owner first, then co-hosts (deduplicated)
  const ownerKey =
    data.owner.__typename === 'Guild' || data.owner.__typename === 'User'
      ? `${data.owner.__typename}:${data.owner.slugId}`
      : null

  const allHosts: Array<{
    typename: 'Guild' | 'User'
    name: string
    slugId: string
    hostRef: any
  }> = []

  if (data.owner.__typename === 'Guild') {
    allHosts.push({
      typename: 'Guild',
      name: data.owner.name,
      slugId: data.owner.slugId,
      hostRef: data.owner,
    })
  } else if (data.owner.__typename === 'User') {
    allHosts.push({
      typename: 'User',
      name: `${data.owner.firstName} ${data.owner.lastName}`,
      slugId: data.owner.slugId,
      hostRef: data.owner,
    })
  }

  for (const edge of data.confirmedHosts?.edges ?? []) {
    const host = edge?.node?.host
    if (!host) continue
    if (host.__typename !== 'Guild' && host.__typename !== 'User') continue
    const key = `${host.__typename}:${host.slugId}`
    if (ownerKey && key === ownerKey) continue

    if (host.__typename === 'Guild') {
      allHosts.push({
        typename: 'Guild',
        name: host.name,
        slugId: host.slugId,
        hostRef: host,
      })
    } else {
      allHosts.push({
        typename: 'User',
        name: `${host.firstName} ${host.lastName}`,
        slugId: host.slugId,
        hostRef: host,
      })
    }
  }

  if (allHosts.length === 0) return null

  // Single host: original layout — large avatar + "Hosted by" + name
  if (allHosts.length === 1) {
    const host = allHosts[0]
    return (
      <Link href={`/${host.slugId}`} underlineHover={false}>
        <XStack gap='$4' alignItems='center'>
          {host.typename === 'Guild' ? (
            host.hostRef.primaryPhoto ? (
              <GuildPrimaryPhoto
                guild={host.hostRef}
                size='$10'
                borderWidth={0}
                // @ts-expect-error boxShadow is web-only
                boxShadow='$boxShadowTight'
              />
            ) : (
              <YStack
                width='$10'
                height='$10'
                alignItems='center'
                justifyContent='center'
                backgroundColor='$background'
                borderRadius='$2'
                // @ts-expect-error boxShadow is web-only
                boxShadow='$boxShadowLight'
              >
                <Icon name='people' size='$7' color='$color' />
              </YStack>
            )
          ) : (
            <UserPrimaryPhoto
              user={host.hostRef}
              size='$10'
              // @ts-expect-error boxShadow is web-only
              boxShadow='$boxShadowTight'
            />
          )}

          <YStack flex={1}>
            <Paragraph size='$4' color='$colorMuted' fontWeight='$3'>
              <Trans>Hosted by</Trans>
            </Paragraph>
            <Paragraph size='$5' fontWeight='$5' flex={1}>
              {host.name}
            </Paragraph>
          </YStack>
        </XStack>
      </Link>
    )
  }

  // Multiple hosts: people icon + compact vertical list
  const glassStyle = useGlass
    ? {
        backgroundColor: isDark
          ? 'rgba(0, 0, 0, 0.35)'
          : 'rgba(255, 255, 255, 0.45)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
      }
    : undefined

  return (
    <XStack gap='$4' alignItems='flex-start'>
      <YStack
        width='$10'
        height='$10'
        alignItems='center'
        justifyContent='center'
        borderRadius='$2'
        backgroundColor={useGlass ? undefined : '$background'}
        // @ts-expect-error boxShadow is web-only
        boxShadow='$boxShadowLight'
        style={glassStyle}
      >
        <Icon name='people' size='$7' color='$color' />
      </YStack>

      <YStack gap='$2' flex={1}>
        <Paragraph size='$4' color='$colorMuted' fontWeight='$3'>
          <Trans>Hosted by</Trans>
        </Paragraph>

        {allHosts.map((host) => (
          <Link
            key={`${host.typename}:${host.slugId}`}
            href={`/${host.slugId}`}
            underlineHover={false}
          >
            <XStack gap='$2' alignItems='center'>
              {host.typename === 'Guild' ? (
                <GuildPrimaryPhoto
                  guild={host.hostRef}
                  size='$6'
                  borderWidth={0}
                />
              ) : (
                <UserPrimaryPhoto user={host.hostRef} size='$6' />
              )}
              <Paragraph size='$5' fontWeight='$5'>
                {host.name}
              </Paragraph>
            </XStack>
          </Link>
        ))}
      </YStack>
    </XStack>
  )
}
